import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Accordion from "../../../../components/accordion/Accordion";
import AddNewItemButton from "../../../../components/buttons/AddNewItemButton";
import ConfirmationModal from "../../../../components/modals/ConfirmationModal";
import {
  updateContacts,
  updateContactsErrors,
  updateOpenContacts,
} from "../../../../redux/actions/CompanyDirectoryActions";
import {
  getContactErrors,
  getContactFieldError,
  isEmptyContact,
  validateContact,
} from "../../../../services-logic/company-directory/company-directory-logic";
import { generateUuid } from "../../../../utils/utilContactsParser";
import { postOpenModal } from "../../../../utils/utilPostMessage";
import ContactsAccordionBody from "./ContactsAccordionBody";
import ContactsAccordionHeader from "./ContactsAccordionHeader";
import AlertMessage from "../../../../components/alerts/AlertMessage";

const ContactsAccordion = () => {
  const dispatch = useDispatch();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);

  const { contactErrors, openContacts, updatedContacts, config, contactsAccordionError } = useSelector(
    (state) => state.companyDirectory
  );
  const { externalOverlay } = useSelector((state) => state.info);

  const [modalStyles, setModalStyles] = useState("");
  const headerRefs = useRef([]); // Array to hold refs for each header

  // Initialize headerRefs based on updatedKnowledgeBaseItems length
  useEffect(() => {
    headerRefs.current = updatedContacts.map((_, i) => headerRefs.current[i] || React.createRef());
  }, [updatedContacts]);

  useEffect(() => {
    const contactErrors = updatedContacts.map((contact) => {
      return getContactErrors(contact);
    });
    dispatch(updateContactsErrors(contactErrors));
  }, []);

  const onUpdateOpenContacts = (openContacts) => {
    dispatch(updateOpenContacts(openContacts));
  };

  const onAddNewContact = () => {
    const contacts = [...updatedContacts];
    contacts.push({
      contactId: generateUuid(),
      name: "",
      description: "",
      destinations: [],
      dtmfShortcutKey: "",
      isDefault: false,
    });
    dispatch(updateContacts(contacts));
    onUpdateOpenContacts([contacts.length - 1]);
  };

  const onContactUpdated = (item, index, key, value) => {
    const updatedItems = [...updatedContacts];
    updatedItems[index] = {
      ...updatedItems[index],
      [key]: value,
    };
    dispatch(updateContacts(updatedItems));

    const updatedContactErrors = [...contactErrors];
    updatedContactErrors[index] = {
      ...updatedContactErrors[index],
      [key]: getContactFieldError(updatedItems[index], key, value),
      noDestinations: "",
    };
    dispatch(updateContactsErrors(updatedContactErrors));
  };

  const onSetAsDefault = (item, value, index) => {
    const updatedItems = updatedContacts.map((contact, i) => ({
      ...contact,
      isDefault: i === value,
    }));
    dispatch(updateContacts(updatedItems));

    var updatedContactErrors = [...contactErrors];
    updatedContactErrors = updatedContactErrors.map((error, i) => ({
      ...error,
      noDestinations: "",
      destinations: getContactFieldError(updatedItems[i], "destinations", updatedItems[i]?.destinations),
    }));
    dispatch(updateContactsErrors(updatedContactErrors));
  };

  const showRemoveContactModal = (index) => {
    if (isEmptyContact(updatedContacts[index])) {
      onRemoveContact(index);
      return;
    }

    if (externalOverlay) {
      const message = {
        childrenType: "confirmation",
        id: "delete-contact",
        index: index,
        headerTitle: "Delete Contact",
        bodyMessage: `Are you sure you want to delete this contact: ${updatedContacts[index].name}?`,
        cancelText: "Cancel",
        confirmText: "Delete",
      };
      postOpenModal(message);
    } else {
      const headerRef = headerRefs.current[index];
      if (headerRef && headerRef.current && typeof headerRef.current.getBoundingClientRect === "function") {
        const accordionRect = headerRef.current.getBoundingClientRect();

        setModalStyles({
          position: "absolute",
          top: `${accordionRect.top + 20}px `,
          left: "50%",
          transform: "translateX(-50%)",
        });
      } else {
        setModalStyles({});
      }
      setIsConfirmationModalOpen(true);
      setDeletingIndex(index);
    }
  };

  const onRemoveContact = (index) => {
    const contacts = updatedContacts;
    contacts.splice(index, 1);
    dispatch(updateContacts(contacts));
    onUpdateOpenContacts([]);

    const updatedContactErrors = [...contactErrors];
    updatedContactErrors.splice(index, 1);
    dispatch(updateContactsErrors(updatedContactErrors));
  };

  useEffect(() => {
    if (externalOverlay) {
      const handleMessage = (event) => {
        const data = event.data;
        if (data.type === "overlay-to-snippet") {
          const extra = data.extra;
          if (extra.id === "delete-contact" && extra.action === "confirm") {
            onRemoveContact(extra.index);
          }
        }
      };

      // Add the event listener
      window.addEventListener("message", handleMessage);

      // Clean up the event listener on component unmount or when externalOverlay changes
      return () => {
        window.removeEventListener("message", handleMessage);
      };
    }
  }, [externalOverlay, onRemoveContact]);

  const contactsAccordionHeader = ({ item, index, isActive }) => {
    return (
      <ContactsAccordionHeader
        ref={headerRefs.current[index]}
        item={item}
        index={index}
        isActive={isActive}
        onSetAsDefault={(value) => onSetAsDefault(item, value, index)}
        onRemoveContact={showRemoveContactModal}
      />
    );
  };

  const contactsAccordionBody = ({ item, index }) => {
    return (
      <ContactsAccordionBody
        item={item}
        index={index}
        onContactUpdated={(index, key, value) => onContactUpdated(item, index, key, value)}
        onRemoveContact={showRemoveContactModal}
      />
    );
  };

  const isAllContactsValid = useMemo(
    () => updatedContacts?.every((contact) => validateContact(null, contact).valid),
    [updatedContacts]
  );

  return (
    <div className="flex flex-col w-full gap-4">
      {contactsAccordionError && <AlertMessage message={contactsAccordionError} />}
      <Accordion
        id="contacts-accordion"
        data={updatedContacts || []}
        headerChildren={contactsAccordionHeader}
        bodyChildren={contactsAccordionBody}
        activeIndexes={openContacts}
        setActiveIndexes={onUpdateOpenContacts}
        itemId="contact-{index}"
      />
      <div className="flex flex-col md:flex-row justify-start items-start md:items-center">
        <AddNewItemButton
          title="+ Add contact"
          width="w-48"
          onClick={onAddNewContact}
          disabled={!isAllContactsValid || updatedContacts?.length >= config?.maxContactListSize}
          onDisabledTooltip={
            !isAllContactsValid && updatedContacts?.length < config?.maxContactListSize
              ? "Finish filling out incomplete items before adding a new one"
              : ""
          }
        />
        {updatedContacts?.length >= config?.maxContactListSize && (
          <span className="text-[10px] md:text-[12px]">
            You have reached the maximum number of contacts allowed for your account.
          </span>
        )}
      </div>
      {!externalOverlay && isConfirmationModalOpen && (
        <ConfirmationModal
          onClose={() => setIsConfirmationModalOpen(false)}
          dataFromSnippet={{
            headerTitle: "Delete Contact",
            bodyMessage: `Are you sure you want to delete this contact: ${updatedContacts[deletingIndex].name}?`,
            cancelText: "Cancel",
            confirmText: "Delete",
          }}
          onConfirm={() => onRemoveContact(deletingIndex)}
          onCancel={() => setIsConfirmationModalOpen(false)}
          modalStyles={modalStyles}
        />
      )}
    </div>
  );
};

export default ContactsAccordion;
